import * as React from 'react';
import { FormField } from 'ui/formField';
import { PrimaryButton, BUTTON_SIZES } from 'ui/button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  MODAL_SIZES,
  ModalFooter,
  MODAL_VARIANTS,
  ModalConfirmationFooter,
  ConfirmationPrompt,
} from 'ui/modal';
import { EmptyStateJourneyGoals } from 'ui/illustrations';
import { CAMPAIGN_DATE_FORMAT, getNewDate } from 'utils/date';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { InputField, TextAreaField } from 'lib/iterable-hook-form';
import {
  JourneyConversion,
  JourneyConversions,
  conversionSchema,
  defaultConversionValues,
} from 'modules/journey/components/JourneyConversions';
import { AttributionMode } from 'modules/domains/campaign';
import { EditableLabels } from 'modules/campaigns/lib/components/EditableLabels';
import styled from 'styled-components';
import { body2, colors, fontSizes, monospaceCaption, spacing } from 'ui/theme';
import { useSelector } from 'react-redux';
import { getFeatureFlags } from 'core/userContext';
import { pxToRem } from 'utils/convert';
import { ErrorMessage } from 'modules/dynamicContentBuilder/components/errorBoundary/ErrorMessage';
import { WORKFLOW_DESCRIPTION_MAX_CHAR_COUNT } from '../constants';

const JourneyConversionsWrapper = styled.div`
  display: flex;
  margin-top: ${spacing.base};
  padding: 8px 16px 16px 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${colors.backgroundSecondary};
  gap: ${spacing.half};
`;

export const StyledConversionWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const StyledConversionsHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.half};
`;

export const StyledConversionDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.base};
`;

export const StyledConversionTitle = styled.div`
  color: ${colors.textPrimary};
  font-size: ${pxToRem(18)};
  font-style: normal;
  font-weight: 700;
  line-height: ${pxToRem(20)};
`;

export const StyledConversionDescription = styled.div`
  color: ${colors.secondaryText};
  font-size: ${fontSizes.s14};
  font-style: normal;
  font-weight: 400;
  line-height: ${pxToRem(20)};
`;

export const StyledAttributionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.threeEights};
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  input {
    width: ${pxToRem(50)};
  }
`;

export const RadioRow = styled.div`
  ${body2}
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const ErrorWrapper = styled.div`
  display: flex;
  margin-left: ${pxToRem(32)};
`;
export const AttributeDetails = styled.div`
  ${body2}
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const CodeBlock = styled.code`
  ${monospaceCaption};
  margin-left: ${spacing.quarter};
  color: ${colors.textPrimary};
  background: ${colors.backgroundTertiary};
  border: 1px solid ${colors.borderPrimary};
  border-radius: 4px;
  display: flex;
  padding: ${spacing.eighth} ${spacing.quarter};
  align-items: center;
  gap: ${spacing.half};
`;

export const ErrorText = styled(ErrorMessage)`
  margin: 0;
`;

const StyledFormField = styled(FormField)`
  margin-bottom: ${spacing.half};
`;

export type NewWorkflowDialogResult = {
  name: string;
  description: string;
  conversions?: JourneyConversion[];
  labels?: string[];
};

type NewWorkflowDialogProps = {
  onClose: () => void;
  onConfirm: (result: NewWorkflowDialogResult) => void;
};

const { useCallback } = React;

export const displayText = {
  modalTitle: 'New journey',
  nameField: {
    label: 'Journey name',
    placeholder: 'Give your journey a name',
  },
  descriptionField: {
    label: 'Description',
    placeholder: 'What are your goals?',
    error: `Must be fewer than ${WORKFLOW_DESCRIPTION_MAX_CHAR_COUNT} characters`,
  },
  cancelButton: 'Cancel',
  confirmButton: 'Create journey',
};

const labelsSchema = z.object({
  labels: z.string().array().optional(),
});

const schema = z
  .object({
    name: z.string({ required_error: 'Name is required' }).min(1),
    description: z.string().max(WORKFLOW_DESCRIPTION_MAX_CHAR_COUNT).optional(),
  })
  .and(conversionSchema)
  .and(labelsSchema);

type NewJourneyFormSchema = Required<z.infer<typeof schema>>;

export const NewJourneyFormProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const methods = useForm<NewJourneyFormSchema>({
    defaultValues: {
      name: `Journey created on ${getNewDate().format(CAMPAIGN_DATE_FORMAT)}`,
      description: '',
      conversions: defaultConversionValues,
      labels: [],
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });
  return <FormProvider {...methods}>{children}</FormProvider>;
};

const NewWorkflowModal = ({ onConfirm, onClose }: NewWorkflowDialogProps) => {
  const isJourneyGoalsEnabled = useSelector(getFeatureFlags).JourneyGoals;
  const isJourneyLabelsEnabled = useSelector(getFeatureFlags).JourneyLabels;

  const [confirmationPromptType, setConfirmationPromptType] = React.useState(
    ConfirmationPrompt.NONE,
  );
  const {
    watch,
    formState: { errors, isDirty },
  } = useFormContext();

  const { description: descriptionError } = errors;
  const [name, description, conversions, labels] = watch([
    'name',
    'description',
    'conversions',
    'labels',
  ]);

  const isAttributionModeExplicitlySet = conversions?.mode === AttributionMode.ExplicitlySet;

  const hasDescriptionError = !!descriptionError;

  const canSubmit = Object.keys(errors).length === 0;

  const onHandleConfirm = useCallback(() => {
    onConfirm({
      name,
      description,
      ...(isJourneyGoalsEnabled && { conversions: [conversions] }),
      ...(isJourneyLabelsEnabled && { labels }),
    });
  }, [name, conversions, description, labels, onConfirm]);

  const handleDiscard = () => {
    onClose();
    setConfirmationPromptType(ConfirmationPrompt.NONE);
  };
  const handleCancel = () => {
    if (isDirty) {
      setConfirmationPromptType(ConfirmationPrompt.DISCARD);
    } else {
      onClose();
    }
  };
  const handleResume = () => {
    setConfirmationPromptType(ConfirmationPrompt.NONE);
  };
  const handleConfirm = () => {
    onHandleConfirm();
  };
  const handleSave = () => {
    if (isAttributionModeExplicitlySet) {
      setConfirmationPromptType(ConfirmationPrompt.CONFIRM);
    } else {
      onHandleConfirm();
    }
  };

  return (
    <Modal
      withPortal
      data-test="new-workflow-modal-wrapper"
      id="new-workflow-modal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader data-test="new-workflow-modal-header">{displayText.modalTitle}</ModalHeader>
      <ModalBody data-test="new-workflow-modal-body" id="new-workflow-modal-body">
        <StyledFormField
          hasError={false}
          inputId="new-workflow-name"
          isRequired
          labelText={displayText.nameField.label}
        >
          <InputField
            id="new-workflow-name"
            name="name"
            placeholder={displayText.nameField.placeholder}
            type="text"
          />
        </StyledFormField>
        {isJourneyLabelsEnabled && <EditableLabels />}
        {!isJourneyGoalsEnabled && (
          <FormField
            hasError={hasDescriptionError}
            errorText={displayText.descriptionField.error}
            inputId="new-workflow-description"
            labelText={displayText.descriptionField.label}
          >
            <TextAreaField
              id="new-workflow-description"
              name="description"
              placeholder={displayText.descriptionField.placeholder}
              isCharacterCountShown
              maxLength={WORKFLOW_DESCRIPTION_MAX_CHAR_COUNT}
            />
          </FormField>
        )}
        {isJourneyGoalsEnabled && (
          <JourneyConversionsWrapper>
            <StyledConversionsHeader>
              <StyledConversionDetails>
                <StyledConversionTitle>Journey Conversion</StyledConversionTitle>
                <StyledConversionDescription>
                  Track your journey&apos;s success by adding a conversion event to the journey and
                  its associated campaigns.
                </StyledConversionDescription>
                <JourneyConversions />
              </StyledConversionDetails>
              <EmptyStateJourneyGoals height={128} width={128} />
            </StyledConversionsHeader>
          </JourneyConversionsWrapper>
        )}
      </ModalBody>
      {isJourneyGoalsEnabled ? (
        <ModalConfirmationFooter
          data-test="new-workflow-modal-actions"
          id="new-workflow-modal-actions"
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          onDiscard={handleDiscard}
          onResume={handleResume}
          confirmLabel="Heads up: "
          confirmHelpText="With the attribution option you selected, you must include the campaignId field in the campaign conversion event you send to Iterable. (Otherwise, any conversions from the campaign won’t be counted.)"
          promptType={confirmationPromptType}
          modalSize={MODAL_SIZES.MEDIUM}
        >
          <PrimaryButton onClick={handleSave} isDisabled={!canSubmit} size={BUTTON_SIZES.LARGE}>
            {displayText.confirmButton}
          </PrimaryButton>
        </ModalConfirmationFooter>
      ) : (
        <ModalFooter data-test="new-workflow-modal-actions" id="new-workflow-modal-actions">
          <PrimaryButton
            onClick={onHandleConfirm}
            isDisabled={!canSubmit}
            size={BUTTON_SIZES.LARGE}
          >
            {displayText.confirmButton}
          </PrimaryButton>
        </ModalFooter>
      )}
    </Modal>
  );
};

export const NewWorkflowDialog = (props: NewWorkflowDialogProps) => {
  return (
    <NewJourneyFormProvider>
      <NewWorkflowModal {...props} />
    </NewJourneyFormProvider>
  );
};
